/**
 * Formatos de tarjeta CDVI (byte8 del registro de tarjeta en el panel).
 * Misma tabla que el API (api/helpers/card-format.js) y el conector: el 26-bit se arma
 * con FC + codigo; los formatos hex guardan el numero de la tarjeta tal cual (hex canonico,
 * en mayusculas y con el ancho del formato, igual que lo muestra Atrium).
 */
export enum CardFormat {
    W26 = 0,
    MIFARE_CLASSIC_HEX = 17,
    // Solo con master A22K (lectores KRYPTO) y conector que lo soporte: el API lo incluye en card_formats
    DESFIRE_EV2_HEX = 18,
    UNIVERSAL_HEX = 255,
}

/** Digitos hex de cada formato; cualquier otro formato no-26-bit usa 16. */
export const CARD_FORMAT_HEX_DIGITS: { [format: number]: number } = {
    17: 14, // Mifare Classic (HEX)
    18: 14, // DESFire EV2 (HEX)
};
const DEFAULT_HEX_DIGITS = 16;

export type CardHexError = 'invalid' | 'zero' | 'too_long';
export interface CardHexResult {
    ok: boolean;
    hex?: string;
    error?: CardHexError;
}

export function isHexCardFormat(format: any): boolean {
    if (format === null || format === undefined || format === '') {
        return false;
    }
    const n = Number(format);
    return !isNaN(n) && n !== 0;
}

export function hexDigitsFor(format: any): number {
    return CARD_FORMAT_HEX_DIGITS[Number(format)] || DEFAULT_HEX_DIGITS;
}

/**
 * Lleva el numero que escribio el usuario al hex canonico del formato.
 * No recorta nunca (Atrium si lo hace): si sobran digitos es un error.
 * Solo texto: un numero (12345) no se sabe si es decimal o hex, igual que en el API.
 */
export function normalizeCardHex(format: any, raw: any): CardHexResult {
    if (typeof raw !== 'string') {
        return { ok: false, error: 'invalid' };
    }
    let value = raw.trim().replace(/[\s:.\-]/g, '');
    value = value.replace(/^0x/i, '').toUpperCase();
    if (!/^[0-9A-F]+$/.test(value)) {
        return { ok: false, error: 'invalid' };
    }
    value = value.replace(/^0+/, '');
    if (value === '') {
        return { ok: false, error: 'zero' };
    }
    const digits = hexDigitsFor(format);
    if (value.length > digits) {
        return { ok: false, error: 'too_long' };
    }
    return { ok: true, hex: value.padStart(digits, '0') };
}

/** Clave de comparacion de 16 digitos (numero de 64 bits). */
export function canonicalCardHex16(hex: any): string {
    return String(hex === null || hex === undefined ? '' : hex).toUpperCase().padStart(16, '0');
}

/**
 * card_formats de get-user-buildings / master_card_formats de los settings.
 * pg puede entregarlo como arreglo o como literal '{0,17}'; sin dato = solo 26-bit.
 */
export function parseCardFormats(raw: any): number[] {
    let list: any[] = [];
    if (Array.isArray(raw)) {
        list = raw;
    } else if (typeof raw === 'string' && raw.trim() !== '') {
        list = raw.replace(/[{}\s]/g, '').split(',');
    }
    const formats = list
        .map(f => Number(f))
        .filter(f => Number.isInteger(f) && f >= 0);
    return formats.length > 0 ? formats : [CardFormat.W26];
}

/**
 * Marca y formatos del panel donde se va a crear la tarjeta, para el modal de alta.
 * Si el conector elegido es el del master (sitio asociado), son los del master
 * (master_vendor_brand / master_card_formats de ma_join_to_request_send).
 */
export function cardPanelData(building: any, masterJoin: any, connector: any): { vendorBrand: string, cardFormats: number[] } {
    const fromMaster = !!masterJoin && !!connector && connector === masterJoin.master_goutru_connector &&
        (!building || connector !== building.goutru_connector);
    if (fromMaster) {
        return { vendorBrand: masterJoin.master_vendor_brand || null, cardFormats: parseCardFormats(masterJoin.master_card_formats) };
    }
    return {
        vendorBrand: building ? building.vendorBrand || null : null,
        cardFormats: parseCardFormats(building ? building.cardFormats : null),
    };
}

/**
 * FC o codigo de una 26-bit con valor: numero o texto de solo digitos. El FC del edificio se
 * precarga como numero (puede ser 0, y 0 != '' es false en JS); vacio o null no cuentan.
 */
export function isCardCodePart(value: any): boolean {
    return value !== null && value !== undefined && /^\d+$/.test(String(value).trim());
}

/**
 * Validador del numero de una tarjeta hex con la misma regla que el envio (normalizeCardHex):
 * acepta separadores, 0x, minusculas y ceros de mas a la izquierda, y nunca recorta.
 * Errores: required (vacio), invalid (no es hex), too_long (mas digitos que el formato), zero.
 */
export function cardHexValidator(format: any): (control: { value: any }) => { [key: string]: any } | null {
    return (control: { value: any }) => {
        const value = control ? control.value : null;
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            return { required: true };
        }
        const result = normalizeCardHex(format, value);
        return result.ok ? null : { [result.error]: true };
    };
}
